import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';
import { productCategories } from '../mock-products';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(false);
  const location = useLocation();
  const { language, switchLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.products'), path: '/products', hasDropdown: true },
    { name: t('nav.industries'), path: '/industries' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const toggleLanguage = () => {
    switchLanguage(language === 'id' ? 'en' : 'id');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg">
              <img 
                src="/img/logo.png"
                alt="PT. Gurita Mandala Persada Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden md:block">
              <div className="text-gray-900 font-bold text-lg leading-tight">
                PT. Gurita Mandala Persada
              </div>
              <div className="text-gray-600 text-xs">Industrial Supply Company</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              item.hasDropdown ? (
                <div 
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setIsProductDropdownOpen(true)}
                  onMouseLeave={() => setIsProductDropdownOpen(false)}
                >
                  <Link
                    to={item.path}
                    className={`text-sm font-medium transition-colors hover:text-[#0693E3] flex items-center gap-1 ${
                      location.pathname.startsWith('/products')
                        ? 'text-[#0693E3]'
                        : 'text-gray-700'
                    }`}
                  >
                    {item.name}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${isProductDropdownOpen ? 'rotate-180' : ''}`} />
                  </Link>
                  
                  {/* Dropdown Menu */}
                  {isProductDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                      {productCategories.map((category) => (
                        <Link
                          key={category.slug}
                          to={`/products/${category.slug}`}
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[#0693E3]/10 hover:text-[#0693E3] transition-colors"
                        >
                          <span className="font-medium">
                            {language === 'id' ? category.nameId : category.name}
                          </span>
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <Link
                          to="/products"
                          className="flex items-center px-4 py-3 text-sm font-semibold text-[#0693E3] hover:bg-[#0693E3]/10 transition-colors"
                        >
                          {language === 'id' ? 'Lihat Semua Produk' : 'View All Products'}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-[#0693E3] ${
                    location.pathname === item.path
                      ? 'text-[#0693E3]'
                      : 'text-gray-700'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Portal Button */}
            <a 
              href="https://app.guritamandala.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#0693E3] transition-colors border border-gray-300 rounded-lg hover:border-[#0693E3]"
            >
              <span>Portal</span>
              <ExternalLink size={14} />
            </a>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#0693E3] transition-colors border border-gray-300 rounded-lg hover:border-[#0693E3]"
              title={language === 'id' ? 'Switch to English' : 'Ganti ke Bahasa Indonesia'}
            >
              <Globe size={16} />
              <span className="uppercase">{language}</span>
            </button>

            {/* CTA Button */}
            <Link to="/contact">
              <Button className="bg-[#CF2E2E] hover:bg-[#B02525] text-white">
                {t('nav.requestQuotation')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-[#0693E3] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                item.hasDropdown ? (
                  <div key={item.path}>
                    <button
                      onClick={() => setIsMobileProductOpen(!isMobileProductOpen)}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 rounded ${
                        location.pathname.startsWith('/products')
                          ? 'text-[#0693E3] bg-blue-50'
                          : 'text-gray-700'
                      }`}
                    >
                      {item.name}
                      <ChevronDown size={16} className={`transition-transform duration-200 ${isMobileProductOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Mobile Product Submenu */}
                    {isMobileProductOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#0693E3]/20">
                        {productCategories.map((category) => (
                          <Link
                            key={category.slug}
                            to={`/products/${category.slug}`}
                            className="block px-4 py-2 text-sm text-gray-600 hover:text-[#0693E3] hover:bg-gray-50 rounded"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {language === 'id' ? category.nameId : category.name}
                          </Link>
                        ))}
                        <Link
                          to="/products"
                          className="block px-4 py-2 text-sm font-semibold text-[#0693E3] hover:bg-gray-50 rounded"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {language === 'id' ? 'Lihat Semua Produk' : 'View All Products'}
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 rounded ${
                      location.pathname === item.path
                        ? 'text-[#0693E3] bg-blue-50'
                        : 'text-gray-700'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              
              {/* Mobile Portal Link */}
              <a
                href="https://app.guritamandala.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
              >
                <span>Portal</span>
                <ExternalLink size={14} />
              </a>
              
              {/* Mobile Language Switcher */}
              <button
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
              >
                <Globe size={16} />
                <span>{language === 'id' ? 'English' : 'Bahasa Indonesia'}</span>
              </button>

              <div className="px-4 pt-2">
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#CF2E2E] hover:bg-[#B02525] text-white">
                    {t('nav.requestQuotation')}
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;